import type { GoalKey, IndustryKey } from './template-goals';

import { COMPOSED_TEMPLATE_ENTRIES } from './template-catalog-composed';

export type TemplateEntry = {
  readonly slug: string;
  readonly theme: string;
  readonly displayName: string;
  readonly category: string;
  readonly description: string;
  readonly thumb: string;
  readonly previewFixture: string;
  readonly previewPath: string;
  readonly fit: string;
  readonly features: readonly string[];
  // --- v2 fields (template experience redesign, plan §3.2). All optional
  // and additive: existing catalog entries are unaffected until populated
  // by later phases (P4 copywriting). ---
  /** Primary goal this template is designed around (Jobs-to-be-Done). */
  readonly goal?: GoalKey;
  /** Industry profile key, aligned with `category` (see CATEGORY_TO_INDUSTRY). */
  readonly industry?: IndustryKey;
  /** Sub-industry content-pack options (e.g. ネイル / ラーメン / 税理士). */
  readonly subIndustries?: readonly string[];
  /** One-paragraph "what your business looks like after launch" story. */
  readonly outcomeStory?: string;
  /** Worry -> answer pairs addressing owner concerns before they sign up. */
  readonly concerns?: readonly { worry: string; answer: string }[];
  /** A day-in-the-life of running the site after launch (3-5 steps). */
  readonly businessFlow?: readonly string[];
};

export type TemplateSlug = TemplateEntry['slug'];

/**
 * Catalog entry as authored — `previewPath` is derived automatically from
 * the slug when TEMPLATE_CATALOG is assembled, so authored entries (including
 * the per-industry composed files in ./template-catalog-composed/) omit it.
 */
export type TemplateEntryDraft = Omit<TemplateEntry, 'previewPath'>;

export const TEMPLATE_CATALOG: readonly TemplateEntry[] = Object.freeze(
  ([] as TemplateEntryDraft[]).concat(COMPOSED_TEMPLATE_ENTRIES as never[]).map((entry) => {
    return Object.freeze({
      ...entry,
      previewPath: `/template-previews/${entry.slug}`,
    });
  }) as readonly TemplateEntry[],
);

export const TEMPLATE_SLUGS = TEMPLATE_CATALOG.map((t) => t.slug) as [string, ...string[]];

export const TEMPLATE_BY_SLUG: ReadonlyMap<string, TemplateEntry> = new Map(
  TEMPLATE_CATALOG.map((t) => [t.slug, t]),
);

export function findTemplateBySlug(slug: string): TemplateEntry | null {
  return TEMPLATE_BY_SLUG.get(slug) ?? null;
}
